import {
  LEAVE_AREA_GROUP_LABELS,
  LEAVE_AREA_LABELS,
  LeaveAreaGroup,
  type LeaveAreaCatalogItem,
  type LeaveAreaGroup as AreaGroup,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { ApiRequestError } from '@/lib/api';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { useLeaveAreas, usePatchLeaveArea } from '@/products/leave/features/use-leave-areas';

const GROUP_ORDER: AreaGroup[] = [
  LeaveAreaGroup.DAILY,
  LeaveAreaGroup.OPERATIONS,
  LeaveAreaGroup.CLOSING,
];

export function LeaveAreasPanel() {
  const areas = useLeaveAreas();
  const patch = usePatchLeaveArea();
  const [pendingOff, setPendingOff] = useState<LeaveAreaCatalogItem | null>(null);
  const items = areas.data?.items ?? [];

  async function apply(code: LeaveAreaCatalogItem['code'], enabled: boolean) {
    try {
      const result = await patch.mutateAsync({ code, enabled });
      const turnedOff = result.changed.filter((item) =>
        result.items.some((row) => row.code === item && !row.enabled),
      );
      const names = (turnedOff.length > 0 ? turnedOff : [code]).map(
        (item) => LEAVE_AREA_LABELS[item],
      );
      toast.success(
        enabled
          ? `${LEAVE_AREA_LABELS[code]} ist eingeschaltet.`
          : `${names.join(' und ')} ${names.length > 1 ? 'sind' : 'ist'} ausgeschaltet.`,
      );
      setPendingOff(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Bereich konnte nicht geändert werden.',
      );
    }
  }

  function onCheckedChange(item: LeaveAreaCatalogItem, enabled: boolean) {
    if (enabled) {
      void apply(item.code, true);
      return;
    }
    const dependentsOn = item.dependents.filter((code) =>
      items.some((row) => row.code === code && row.enabled),
    );
    if (dependentsOn.length > 0) {
      setPendingOff(item);
      return;
    }
    void apply(item.code, false);
  }

  const pendingNames =
    pendingOff?.dependents
      .filter((code) => items.some((row) => row.code === code && row.enabled))
      .map((code) => LEAVE_AREA_LABELS[code]) ?? [];

  return (
    <section className="space-y-4" aria-labelledby="leave-areas-heading">
      <div>
        <h2 id="leave-areas-heading" className="text-base font-semibold">
          Bereiche
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ausgeschaltete Bereiche lehnen neue Buchungen ab. Vorhandene Anträge bleiben entscheidbar.
        </p>
      </div>
      {areas.isPending ? (
        <p className="text-sm text-muted-foreground">Bereiche werden geladen …</p>
      ) : null}
      {areas.isError ? (
        <p className="text-sm text-destructive">
          {areas.error instanceof ApiRequestError
            ? areas.error.message
            : 'Die Bereiche konnten nicht geladen werden.'}
        </p>
      ) : null}
      {GROUP_ORDER.map((group) => {
        const groupItems = items.filter((item) => item.group === group);
        if (groupItems.length === 0) {
          return null;
        }
        return (
          <div key={group} className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {LEAVE_AREA_GROUP_LABELS[group]}
            </h3>
            <ul className="space-y-2">
              {groupItems.map((item) => {
                const titleId = `leave-area-${item.code}`;
                const busy = patch.isPending && patch.variables?.code === item.code;
                return (
                  <li
                    key={item.code}
                    className={`flex items-start justify-between gap-4 ${leavePanelClassName}`}
                  >
                    <div>
                      <p id={titleId} className="font-medium">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.hides.join(' · ')}</p>
                    </div>
                    <Switch
                      checked={item.enabled}
                      disabled={busy}
                      aria-labelledby={titleId}
                      onCheckedChange={(checked) => onCheckedChange(item, checked)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      <AlertDialog
        open={pendingOff !== null}
        onOpenChange={(open) => {
          if (!open && !patch.isPending) {
            setPendingOff(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingOff ? `${pendingOff.label} ausschalten?` : 'Bereich ausschalten?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingNames.length > 0
                ? `Wird ebenfalls ausgeschaltet: ${pendingNames.join(', ')}. Vorhandene Einträge bleiben entscheidbar.`
                : 'Vorhandene Einträge bleiben entscheidbar.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (!pendingOff) {
                  return;
                }
                void apply(pendingOff.code, false);
              }}
            >
              Ausschalten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
